import { waitAFrame } from '@goodboydigital/astro';
import p2 from 'p2';
import { Container, Sprite, Texture } from 'pixi.js';

import { Shared } from '../../App';
import { Maths } from '../../utils/Maths';
import { Random } from '../../utils/Random';
import { CullingComponent } from '../components/CullingComponent';
import { DepthOrder, Game } from '../Game';
import { PhysicsCollisionGroup, PhysicsShapeType, PhysicsUtils } from '../physics';
import { PhysicsComponent } from '../physics/PhysicsComponent';
import { BaseEntity } from './BaseEntity';

export interface CoinEntityParams
{
    type: string,
    x: number,
    y: number,
    w: number,
    h: number,
    sx: number,
    sy: number,
    r: number,
    img: string,
    props?: {
        secret?: boolean
        bonus?: boolean
    }
}

export class CoinEntity extends BaseEntity
{
    public static frames: Texture[] = [];
    public sprite!: Container;
    public halo!: Sprite;
    public coin!: Sprite;
    public params!: CoinEntityParams;
    public physics: PhysicsComponent;
    public culling: CullingComponent;
    public collected = false;
    public frame = 0;
    public secret = false;
    public bonus = false;

    constructor()
    {
        super();
        this.physics = this.addComponent(PhysicsComponent);
        this.culling = this.addComponent(CullingComponent);

        this.sprite = new Container();
        this.view.addChild(this.sprite);

        this.halo = new Sprite(Shared.assets.getTexture('coin-halo'));
        this.halo.anchor.set(0.5);
        this.halo.scale.set(2.5);
        this.halo.alpha = 0.3;
        this.sprite.addChild(this.halo);

        this.coin = new Sprite();
        this.coin.anchor.set(0.5);
        this.coin.scale.set(0.75);
        this.sprite.addChild(this.coin);

        if (!CoinEntity.frames.length)
        {
            for (let i = 1; i <= 12; i++)
            {
                const num = `${i}`.padStart(2, '0');

                CoinEntity.frames.push(Shared.assets.getTexture(`coin-${num}`));
            }
        }

        this.physics.culling = true;
        this.physics.physicsFreeUpdateRunner.add(this as any);
        this.culling.updateBounds();
    }

    public build(params: CoinEntityParams): void
    {
        this.physics.clearParts();
        this.params = params;

        const body = PhysicsUtils.createBody({
            x: this.params.x,
            y: this.params.y,
            angle: this.params.r,
            mass: 0,
        }, {
            type: PhysicsShapeType.CIRCLE,
            radius: 50,
            sensor: true,
            group: PhysicsCollisionGroup.OBJECT,
            mask: PhysicsCollisionGroup.VEHICLE,
        });

        this.secret = params.props?.secret ?? false;
        this.physics.addParts(body);
        this.z = DepthOrder.COIN;
        this.bonus = params.props?.bonus ?? false;
        this.levelReset();

        if (this.bonus) this.autoCollect();
    }

    public levelReset(): void
    {
        this.cancelCollectAnimation();
        this.sprite.alpha = 1;
        this.sprite.visible = true;
        this.sprite.renderable = true;
        this.collected = false;
        this.frame = Random.range(0, CoinEntity.frames.length);
        this.sprite.x = 0;
        this.sprite.y = 0;
        this.sprite.scale.set(this.secret ? 0 : 1);
        this.view.addChild(this.sprite);
    }

    protected onBeginContact(other: p2.Body): void
    {
        if (this.bonus) return;
        const game = this.scene as Game;
        const otherEntity = other.physicsComponent?.entity;

        if (this.collected || this.secret || !game.race.running || !game.vehicle.assembled) return;

        if (otherEntity === game.vehicle.character || otherEntity === game.vehicle.chassis)
        {
            this.collect();
        }
    }

    public async reveal(delay = 0): Promise<void>
    {
        if (this.collected || !this.secret) return;
        this.secret = false;
        this.sprite.alpha = 1;
        this.sprite.scale.set(0);

        this.game?.tween.kill(this.sprite.scale);
        await this.game?.tween.to(this.sprite.scale, {
            x: 1, y: 1, duration: 0.3, delay, ease: 'back.out',
            onStart: () => this.game?.soundSystem.playSfx('pickup', 0.5),
        });
    }

    private async autoCollect(): Promise<void>
    {
        await waitAFrame();
        this.collect();
    }

    public collect(): void
    {
        this.collected = true;
        this.game?.soundSystem.playSfx('pickup');
        this.playCollectAnimation();
    }

    public physicsFreeUpdate(): void
    {
        if (!this.view.renderable || !this.view.visible) return;
        if (!this.collected) this.frame += 0.5;
        this.frame %= CoinEntity.frames.length;
        this.coin.texture = CoinEntity.frames[Math.floor(this.frame)];
    }

    public async playCollectAnimation(): Promise<void>
    {
        if (!this.game) return;
        this.game.hud.view.addChild(this.sprite);

        const globalPos = this.view.getGlobalPosition ? this.view.getGlobalPosition() : this.view.position;
        const pos = this.game.hud.view.toLocal(globalPos);

        this.sprite.x = pos.x;
        this.sprite.y = pos.y;

        const x = this.game.hud.raceCoinsWidget.centerX - 50;
        const y = this.game.hud.raceCoinsWidget.centerY;
        const distance = Maths.distance(this.sprite.x, this.sprite.y, x, y);
        const duration = Maths.clamp(distance * 0.00075, 0.3, 0.7);

        this.game.tween.to(this.sprite.scale, { x: 1.5, y: 1.5,
            duration: duration * 0.5,
            ease: 'sine.out',
        });
        this.game.tween.to(this.sprite.scale, { x: 0.5, y: 0.5,
            duration: duration * 0.5,
            ease: 'sine.in',
            delay: duration * 0.5,
        });
        this.game.tween.to(this, { frame: 0,
            duration: duration * 0.5,
            ease: 'linear',
        });
        this.game.tween.to(this.sprite, { x,
            duration,
            ease: 'expo.inOut',
        });
        await this.game.tween.to(this.sprite, { y,
            duration,
            ease: 'sine.inOut',
        });

        if (this.bonus)
        {
            this.game.race.bonusCoins += 1;
        }
        else
        {
            this.game.race.collectedCoins += 1;
        }

        this.game.hud.raceCoinsWidget.wobble();
        this.sprite.alpha = 0;
    }

    public cancelCollectAnimation(): void
    {
        if (!this.game) return;
        this.game.tween.kill(this);
        this.game.tween.kill(this.sprite);
        this.game.tween.kill(this.sprite.scale);
    }
}

export class CoinFactory
{
    public static match(data: Record<string, any>): boolean
    {
        return data.type === 'SpriteEntity' && data.img === 'coin';
    }

    public static create(game: Game, data: Partial<CoinEntityParams>): void
    {
        const entity = game.pool.get(CoinEntity) as CoinEntity;

        entity.build(data as CoinEntityParams);

        game.level.addEntity(entity);
    }
}
